import { useAtom } from 'jotai';
import { values } from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { authAtom, changeProjectStatusConfirmDialogAtom } from '../atoms';
import { Select } from '../components';
import { Avatar } from '../components/Avatar';
import { PROJECT_STATUS, USER_ROLE } from '../lib/constants';
import { useProjectQuery } from '../lib/queries';
import { PATHS } from '../lib/paths';
import { RemoveUserFromProjectDialog } from '../components/RemoveUserFromProjectConfirmDialog';
import { removeUserFromProjectConfirmDialogAtom } from '../atoms/removeUserFromProjectConfirmDialogAtom';
import { AddUserToProjectDialogAtom } from '../atoms/addUserToProjectDialogAtom';
import { AddUserToProjectDialog } from '../components/AddUserToProjectDialog';

export default function ProjectPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [auth] = useAtom(authAtom);
  const [, setStatusChangeDialogAtom] = useAtom(changeProjectStatusConfirmDialogAtom);
  const [, setRemoveUserFromProject] = useAtom(removeUserFromProjectConfirmDialogAtom);
  const [, setAddUsersToProjectProject] = useAtom(AddUserToProjectDialogAtom);

  const projectQuery = useProjectQuery(params.id!);

  const handleStatusChange = (e: any) => {
    const status = e.target.value;
    if (!projectQuery.data?.project) return;
    if (status !== projectQuery.data.project.status)
      setStatusChangeDialogAtom({ isOpen: true, project: projectQuery.data.project, status });
  };

  const isOptionDisabled = (option: string) => {
    if (!projectQuery.data) return true;
    const options = values(PROJECT_STATUS) as string[];
    const queryIndex = options.indexOf(projectQuery.data.project.status);

    if (queryIndex === options.length - 1) return true;
    if (option === options[queryIndex + 1]) return false;
    return true;
  };

  useEffect(() => {
    if (projectQuery.error) navigate(PATHS.errorPath('404'));
  }, [navigate, projectQuery.error]);

  if (!projectQuery.data) return null;
  return (
    <main className="grid gap-4 w-700 mx-auto">
      <h1 className="text-hero font-bold col-span-2">{t('projectDetails.title')}</h1>

      <span className="font-semibold">{t('project.name')}</span>
      <span>{projectQuery.data.project.name}</span>

      <span className="font-semibold">{t('project.createdAt')}</span>
      <span className="grid">
        <span>{new Date(projectQuery.data.project.createdAt).toLocaleTimeString()}</span>
        <span>{new Date(projectQuery.data.project.createdAt).toLocaleDateString()}</span>
      </span>

      <span className="font-semibold">{t('project.startDate')}</span>
      {projectQuery.data.project.startDate && (
        <span className="grid">
          <span>{new Date(projectQuery.data.project.startDate).toLocaleTimeString()}</span>
          <span>{new Date(projectQuery.data.project.startDate).toLocaleDateString()}</span>
        </span>
      )}
      {!projectQuery.data.project.startDate && <span>-</span>}

      <span className="font-semibold">{t('project.createdAt')}</span>
      {projectQuery.data.project.doneDate && (
        <span className="grid">
          <span>{new Date(projectQuery.data.project.doneDate).toLocaleTimeString()}</span>
          <span>{new Date(projectQuery.data.project.doneDate).toLocaleDateString()}</span>
        </span>
      )}
      {!projectQuery.data.project.doneDate && <span>-</span>}

      <span className="font-semibold">{t('project.key')}</span>
      <span>{projectQuery.data.project.key}</span>

      <span className="font-semibold">{t('project.status.key')}</span>
      {auth?.user?.role === USER_ROLE.SUPERADMIN && (
        <Select
          defaultValue={projectQuery.data.project.status}
          onChange={handleStatusChange}
          disabled={projectQuery.data.project.status === PROJECT_STATUS.ARCHIVED}
        >
          {values(PROJECT_STATUS).map((s) => (
            <option value={s} key={s} disabled={isOptionDisabled(s)}>
              {t(`project.status.${s}`)}
            </option>
          ))}
        </Select>
      )}
      {auth?.user?.role === USER_ROLE.CLIENT && (
        <span>{t(`project.status.${projectQuery.data.project.status}`)}</span>
      )}

      <span className="font-semibold">{t('project.users')}</span>
      <ul>
        {projectQuery.data.project.users.map((u) => {
          return (
            <li key={u.id} className="flex gap-2 items-center my-2">
              <Avatar seed={u.name} />
              <span className="grow">{u.name}</span>
              {u.role !== USER_ROLE.SUPERADMIN && (
                <button
                  aria-label="remove-user-from-project"
                  className="danger font-thin"
                  onClick={() =>
                    setRemoveUserFromProject({
                      isOpen: true,
                      project: projectQuery.data.project,
                      user: u,
                    })
                  }
                >
                  <i className="ri-delete-bin-fill" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <button
        aria-label="add-user-to-project"
        className="col-start-2 primary-outline"
        onClick={() =>
          setAddUsersToProjectProject({ isOpen: true, project: projectQuery.data.project })
        }
      >
        <i className="ri-add-line" />
      </button>
      <RemoveUserFromProjectDialog />
      <AddUserToProjectDialog />
    </main>
  );
}
